import type { DeviceType, Dimension, TourFrame } from '@carboncopyco/types';
import {
  RecorderStatus,
  TourCaptureIncomingMessageType,
  TourCaptureOutgoingMessageType,
} from '@carboncopyco/types';
import { scopedLogger, Scope } from '@carboncopyco/logger';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { v4 as generateUUID } from 'uuid';

import {
  CommunicationService,
  bindSnapshotHotkeys,
  isDesktop,
} from '@carboncopyco/mimesis-shared';
import {
  AnalyticsTrackEventName,
  Debug,
  useAnalytics,
} from '@carboncopyco/utils';

import {
  selectOrganizationId,
  useCreateTourFrameMutation,
  useUpdateTourStatusMutation,
  useUpdateTourWithNewFramesMutation,
  useUploadToursFileMutation,
  useUploadToursResourceMutation,
} from '@carboncopyco/data';
import type { eventWithTime } from '@carboncopyco/rrweb-types';
import { ColorHex, LoadingDots, notify } from '@carboncopyco/ui';
import DimensionPanelWrapper from '../../components/DimensionPanelWrapper';
import { useTourDispatch, useTourSelector } from '../../hooks/store';
import { useProcessImageAsFrame } from '../../hooks/useProcessImageAsFrame';
import useTourCapture from '../../hooks/useTourCapture';
import {
  addTourFrame,
  resetTourFrames,
  selectIsCaptureSnapshotPending,
  selectIsCaptureStarted,
  selectIsCapturing,
  selectIsCapturingFinished,
  selectIsReadyToCapture,
  selectIsShowingDimensionPanel,
  setCanFinishCapturing,
  setIsCaptureButtonDisabled,
  setIsCaptureSnapshotPending,
  setIsCaptureStarted,
  setIsCapturing,
  setIsCapturingFinished,
  setIsReadyToCapture,
  setIsShowingDimensionPanel,
  setTourId,
  updateFramesLoading,
} from '../../store';
import { CAPTURE_IFRAME_ID } from '../../utils/constants';
import { getScreenshotBase64 } from '../../utils/getScreenshotBase64';
import { handleDimensionChange as handleDimensionChangeUtil } from '../../utils/recordUtils';
import { uploadAllResources } from '../../utils/toursResources';
import { useFinishCapturingActionsContext } from './FinishCapturingActionsContext';
import FirstFrameFailedCapturingModal from './FirstFrameFailedCapturingModal';
import { useTourCapturePageProps } from './TourCapturePageContext';
import { localise } from './localise';
import useDimensions from './useDimensions';

const debug = Debug.extend('TourCaptureDynamicRecorder');
const tourCaptureLogger = scopedLogger(Scope.TOUR_CAPTURE);

interface TourCaptureDynamicRecorderFromUrlProps {
  setPageTitle: (pageTitle: string) => void;
  playerToken?: string;
  fileServiceToken?: string;
  tourId?: number;
  url?: string;
  originalTourId?: string;
  tourEditorTabId?: number;
  insertIndex?: number;
  onDimensionChange?: (
    dimension: Dimension,
    type: DeviceType,
    fitFactor: number
  ) => void;
  webViewRef?: React.RefObject<HTMLElement>;
}

const TourCaptureDynamicRecorderFromUrl = ({
  setPageTitle,
  playerToken,
  fileServiceToken,
  tourId,
  url,
  originalTourId,
  tourEditorTabId,
  insertIndex,
  onDimensionChange,
  webViewRef,
}: TourCaptureDynamicRecorderFromUrlProps) => {
  const {
    uuid,
    useNavigate,
    dynamicRecordingUrl,
    fileServiceDynamicLink,
    mimesisReplicateUrl,
    webViewFrame,
    desktopHooks,
    tab,
    activeTabId,
  } = useTourCapturePageProps();
  const { workspaceId } = useParams();
  const { tourSubmitAction, targetWorkspaceId } =
    useFinishCapturingActionsContext();

  const tabManager = desktopHooks?.useTabManager();
  const communicationService = useMemo(
    () => new CommunicationService({ channelId: uuid }),
    [uuid]
  );

  const flags = useFlags();
  const tabId = tab?.id;
  const analytics = useAnalytics();
  const dispatch = useTourDispatch();
  const organizationId = useSelector(selectOrganizationId);
  const isCapturing = useTourSelector(selectIsCapturing(uuid));
  const isCapturingFinished = useTourSelector(selectIsCapturingFinished(uuid));
  const isCaptureStarted = useTourSelector(selectIsCaptureStarted(uuid));
  const [isRRWEBCaptureStarted, setIsRRWEBCaptureStarted] = useState(false);
  const isReadyToCapture = useTourSelector(selectIsReadyToCapture(uuid));
  const isCaptureSnapshotPending = useTourSelector(
    selectIsCaptureSnapshotPending(uuid)
  );
  const isShowingDimensionPanel = useTourSelector(
    selectIsShowingDimensionPanel(uuid)
  );
  const [recorderIframe, setRecorderIframe] = useState<HTMLIFrameElement>(null);
  const currentFrameIndex = useRef(0);
  const [canRecord, setCanRecord] = useBoolean(true);
  const [createTourFrame] = useCreateTourFrameMutation();
  const [uploadToursResource] = useUploadToursResourceMutation();
  const [uploadToursFile] = useUploadToursFileMutation();

  const { uploadAnImage } = useProcessImageAsFrame({
    fileServiceDynamicLink,
  });

  const [frameIdsCaptured, setFramesIdsCaptured] = useState<TourFrame['id'][]>(
    []
  );
  const [uploadedFilesSet, setUploadedFilesSet] = useState<Set<string>>(
    new Set()
  );
  const [visitedUrls, setVisitedUrls] = useState<string[]>([]);
  const [firstFrameTitle, setFirstFrameTitle] = useState<string>();
  const [didCaptureCrash, setDidCaptureCrash] = useState<boolean>(false);
  const [isToursRecorderReady, setIsToursRecorderReady] =
    useState<boolean>(false);
  const [isContinueTourRequested, setIsContinueTourRequested] =
    useState<boolean>(false);
  const [firstFrameFailedCapturing, setFirstFrameFailedCapturing] =
    useState<boolean>(false);
  const [blobFilesToBeReplaced, setBlobFilesToBeReplaced] = useState<
    Set<{ base64: string; blobURI: string }>
  >(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { closeDimensionsPanel } = useDimensions({ uuid });

  const [updateTourWithNewFrames] = useUpdateTourWithNewFramesMutation();

  const { stepConfigToInheritFrom } = useTourCapture({
    originalTourId,
  });

  const showLoadingDots = useMemo(() => {
    return !canRecord || !isToursRecorderReady;
  }, [canRecord, isToursRecorderReady]);

  const addToUploadedFilesSet = (fileName: string) => {
    setUploadedFilesSet((previousSet) => new Set(previousSet).add(fileName));
  };

  const addToBlobFilesToBeReplacedSet = (base64: string, blobURI: string) => {
    setBlobFilesToBeReplaced((previousSet) =>
      new Set(previousSet).add({ base64, blobURI })
    );
  };

  const removeFromUploadedFilesSet = (fileName: string) => {
    setUploadedFilesSet((previousSet) => {
      const newSet = new Set(previousSet);
      if (newSet.has(fileName)) {
        newSet.delete(fileName);
      }
      return newSet;
    });
  };

  const sendSnapshotRequest = () => {
    communicationService.postMessage({
      message: {
        type: TourCaptureIncomingMessageType.requestSnapshot,
      },
      targetWindow: recorderIframe?.contentWindow,
    });
    tourCaptureLogger.info('[Live] Snapshot requested');
  };

  useEffect(() => {
    if (isCapturing) {
      communicationService.postMessage({
        message: {
          type: TourCaptureIncomingMessageType.startTour,
          payload: {
            frameIndex: currentFrameIndex.current,
            listenSnapshotKeyboardEvents: true,
            shouldTakeScreenshots: flags.tourCaptureTakeScreenshots,
            uuid: uuid,
            tourId,
            organizationId,
            fileServiceDynamicLink,
            tabId,
            isDesktop,
          },
        },
        targetWindow: recorderIframe?.contentWindow,
      });
      tourCaptureLogger.info('[Live]: Capture started', {
        tourId,
        organizationId,
      });
      tourCaptureLogger.setContextProperty('tourId', tourId);

      setIsRRWEBCaptureStarted(true);

      bindSnapshotHotkeys(document, sendSnapshotRequest);
      dispatch(setTourId({ uuid, tourId }));
      if (isDesktop && tabId) {
        window.dsDesktop.tour.startCapturing(tabId, {
          tourId,
          salt: tabId,
        });
      }
    }
  }, [isCapturing]);

  useEffect(() => {
    if (recorderIframe?.contentWindow) {
      communicationService.postMessage({
        message: {
          type: TourCaptureIncomingMessageType.tourIsCapturing,
        },
        targetWindow: recorderIframe?.contentWindow,
      });
    }
  }, [recorderIframe?.contentWindow]);

  useEffect(() => {
    if (tourId && isCaptureStarted && firstFrameTitle && !didCaptureCrash) {
      dispatch(
        updateFramesLoading({
          uuid,
          frameLoading: {
            frameIndex: 0,
            isLoading: false,
          },
        })
      );
      dispatch(resetTourFrames({ uuid }));
    }
  }, [isCaptureStarted, tourId, firstFrameTitle, didCaptureCrash]);

  useEffect(() => {
    if (isCapturing) {
      setDidCaptureCrash(false);
      startTour();
    }
    if (!isCapturing && isCaptureStarted && isCapturingFinished) {
      endTour();
    }
    if (!isCapturing && isCaptureStarted && !isCapturingFinished) {
      restartTour();
    }
  }, [isCapturing, isCapturingFinished]);

  useEffect(() => {
    communicationService.onMessage(onPostMessage);
    return () => communicationService.removeMessageListener(onPostMessage);
  }, [
    tourId,
    frameIdsCaptured,
    recorderIframe,
    uploadedFilesSet,
    visitedUrls,
    blobFilesToBeReplaced,
    activeTabId,
    isCaptureStarted,
    isCapturing,
  ]);

  useEffect(() => {
    if (isContinueTourRequested && isToursRecorderReady) {
      setIsContinueTourRequested(false);
      continueTour();
    }
  }, [isContinueTourRequested, isToursRecorderReady]);

  useEffect(() => {
    function onLoad() {
      setIsToursRecorderReady(false);
      setIsContinueTourRequested(true);
    }

    if (
      recorderIframe &&
      isCapturing &&
      isCaptureStarted &&
      isRRWEBCaptureStarted
    ) {
      recorderIframe.onload = onLoad;
    }
  }, [recorderIframe, isCapturing, isCaptureStarted, isRRWEBCaptureStarted]);

  useEffect(() => {
    if (isCaptureSnapshotPending) {
      sendSnapshotRequest();
      dispatch(
        setIsCaptureSnapshotPending({
          uuid,
          isPending: false,
        })
      );
    }
  }, [isCaptureSnapshotPending]);

  const [updateTourStatus] = useUpdateTourStatusMutation();
  const { navigateToTourEditor } = useNavigate();

  const onPostMessage = async (event) => {
    if (event.data) {
      const { data } = event;
      switch (data.status) {
        case TourCaptureOutgoingMessageType.isCapturingTour:
          communicationService.postMessage({
            message: {
              type: TourCaptureIncomingMessageType.tourIsCapturing,
            },
            targetWindow: recorderIframe?.contentWindow,
          });
          break;
        case TourCaptureOutgoingMessageType.URL_CHANGED: {
          debug('onPostMessage', data);
          if (!isDesktop && !recorderIframe) {
            const iframe = document.getElementById(
              'recorder-iframe'
            ) as HTMLIFrameElement;
            setRecorderIframe(iframe);
          }

          if (!isReadyToCapture) {
            dispatch(setIsReadyToCapture({ uuid }));
          } else if (isDesktop && isCaptureStarted && isCapturing) {
            setIsContinueTourRequested(true);
          }

          setVisitedUrls((urls) => [...urls, data.payload.proxyUrl]);
          break;
        }
        case TourCaptureOutgoingMessageType.fileUpload: {
          debug('onPostMessage', data);
          const frameIndex = currentFrameIndex.current;
          currentFrameIndex.current += 1;
          await onNewChunk({
            frameIndex,
            data: data.payload,
          });
          tourCaptureLogger.info('[Live]: Frame uploaded', { frameIndex });
          break;
        }
        case TourCaptureOutgoingMessageType.mimesisReplicateStatus: {
          setIsToursRecorderReady(data.payload.mimesisReplicateReady);
          break;
        }
        case TourCaptureOutgoingMessageType.recordingInProgress: {
          debug('onPostMessage', data);
          setFirstFrameTitle(data.payload.pageTitle);
          setCanRecord.setTrue();
          break;
        }
        case TourCaptureOutgoingMessageType.updatePageTitle: {
          debug('onPostMessage', data);
          setPageTitle(data.title);
          break;
        }
        case TourCaptureOutgoingMessageType.PAGE_NOT_AVAILABLE: {
          communicationService.postMessage({
            message: {
              type: TourCaptureIncomingMessageType.isTour,
            },
            targetWindow: recorderIframe?.contentWindow,
          });
          dispatch(
            setCanFinishCapturing({
              uuid,
              canFinish: false,
            })
          );
          break;
        }
        case TourCaptureOutgoingMessageType.blobToBase64ConversionResult: {
          const { base64, blobURI } = data.payload;
          addToBlobFilesToBeReplacedSet(base64, blobURI);
          break;
        }
      }
    }
  };

  const startTour = async () => {
    dispatch(setIsCaptureStarted({ uuid, isStarted: true }));
    dispatch(setCanFinishCapturing({ uuid, canFinish: true }));
  };
  const continueTour = () => {
    communicationService.postMessage({
      message: {
        type: TourCaptureIncomingMessageType.continueTour,
        payload: {
          frameIndex: currentFrameIndex.current,
          listenSnapshotKeyboardEvents: true,
          tourId,
          organizationId,
          fileServiceDynamicLink,
          tabId,
          isDesktop,
        },
      },
      targetWindow: recorderIframe?.contentWindow,
    });
    tourCaptureLogger.info('[Live]: Capture continued');
    dispatch(setCanFinishCapturing({ uuid, canFinish: true }));
  };
  const endTour = async () => {
    communicationService.postMessage({
      message: {
        type: TourCaptureIncomingMessageType.endTour,
      },
      targetWindow: recorderIframe?.contentWindow,
    });
    tourCaptureLogger.info('[Live]: Capture ended');
  };

  const restartTour = async () => {
    dispatch(setIsCaptureStarted({ uuid, isStarted: false }));

    communicationService.postMessage({
      message: {
        type: TourCaptureIncomingMessageType.restartTour,
      },
      targetWindow: recorderIframe?.contentWindow,
    });
    tourCaptureLogger.info('[Clone]: Capture restarted');
    communicationService.postMessage({
      message: { type: RecorderStatus.RECORDER_RELOAD },
      targetWindow: recorderIframe?.contentWindow,
    });
    dispatch(setCanFinishCapturing({ uuid, canFinish: true }));
    currentFrameIndex.current = 0;
  };

  const handleRestartCapturingAfterFirstFrameFailedCapturing = () => {
    setFirstFrameFailedCapturing(false);
    dispatch(
      setIsCaptureButtonDisabled({
        uuid,
        disabled: false,
      })
    );
    dispatch(setIsCapturing({ uuid, isCapturing: true }));
    dispatch(
      setIsCapturingFinished({
        uuid,
        isCapturingFinished: false,
      })
    );
    dispatch(setIsCaptureStarted({ uuid, isStarted: true }));
  };

  const handleFirstFrameFailedCapturing = (isFirstFrame: boolean) => {
    if (isFirstFrame) {
      dispatch(setIsCaptureButtonDisabled({ uuid, disabled: true }));
      dispatch(setIsCapturing({ uuid, isCapturing: false }));
      dispatch(setIsCaptureStarted({ uuid, isStarted: false }));
      dispatch(resetTourFrames({ uuid }));
      dispatch(setIsReadyToCapture({ uuid }));
      setDidCaptureCrash(true);
      return setFirstFrameFailedCapturing(true);
    }
    return notify.error('Sorry, frame was not created');
  };

  const onNewChunk = async ({
    data,
    frameIndex,
  }: {
    data: any;
    frameIndex: number;
  }) => {
    if (data?.frameData) {
      const events: eventWithTime[] = data.frameData;
      const defaultStep = data.partialNewFrameData.steps[0];
      const originalLayout = defaultStep.config.tooltip.balloon.layout;

      if (stepConfigToInheritFrom) {
        defaultStep.config = {
          ...stepConfigToInheritFrom,
          tooltip: {
            ...stepConfigToInheritFrom.tooltip,
            balloon: {
              ...stepConfigToInheritFrom.tooltip.balloon,
              layout: originalLayout,
            },
          },
        };
      }

      const defaultSteps = [
        {
          ...defaultStep,
          title: 'Untitled step',
          stepId: generateUUID(),
        },
      ];

      let newFrameData = {
        ...data.partialNewFrameData,
        screenshotBase64: await getScreenshotBase64(
          flags.tourCaptureTakeScreenshots,
          Number.parseInt(uuid)
        ),
        frameDataUrl: '',
        tourId,
        frameIndex,
        steps: defaultSteps,
        organizationId,
      };
      if (!data.isLast) {
        dispatch(addTourFrame({ uuid, tourFrame: newFrameData }));
        dispatch(
          updateFramesLoading({
            uuid,
            frameLoading: {
              frameIndex,
              isLoading: true,
            },
          })
        );
      }

      const { compressedEvents, downloadedResourcesArray } = await localise({
        events,
        organizationId,
        tourId,
        blobFilesToBeReplaced,
        fileServiceDynamicLink,
        tabId,
        errorCallback: (error) => {
          analytics.trackEvent(AnalyticsTrackEventName.MIMESIS_SDK_ERROR, {
            tourId,
            functionName: 'getCompressedEvents',
            error,
          });
        },
      });

      uploadToursFile({
        organizationId,
        tourId,
        jsonId: generateUUID(),
        token: fileServiceToken,
        frames: compressedEvents,
      })
        .unwrap()
        .then(async (tourFileUploadResponse) => {
          const urlToJson = tourFileUploadResponse?.url_to_json;
          let imgPath;
          try {
            imgPath = await uploadAnImage(
              newFrameData.screenshotBase64,
              tourId,
              fileServiceToken
            );
          } catch (e) {
            console.log('error in uploading screenshot', e);
          }

          newFrameData = {
            ...newFrameData,
            pageUrl: urlToJson,
            frameDataUrl: urlToJson,
            frameScreenshotUrl: imgPath,
          };

          createTourFrame(newFrameData)
            .unwrap()
            .then((frameResponse) => {
              setFramesIdsCaptured((prevFrames) => [
                ...prevFrames,
                frameResponse.id,
              ]);
              if (!isDesktop) {
                uploadAllResources({
                  resources: downloadedResourcesArray,
                  tourId,
                  fileServiceToken,
                  organizationId,
                  uploadToursResource,
                  uploadedFilesSet,
                  addToUploadedFilesSet,
                  removeFromUploadedFilesSet,
                  onProgress: (progress) => {
                    dispatch(
                      updateFramesLoading({
                        uuid,
                        frameLoading: {
                          frameIndex,
                          progress,
                          isLoading: true,
                        },
                      })
                    );
                  },
                }).then(() => {
                  dispatch(
                    updateFramesLoading({
                      uuid,
                      frameLoading: {
                        frameIndex,
                        isLoading: false,
                      },
                    })
                  );
                });
              } else {
                dispatch(
                  updateFramesLoading({
                    uuid,
                    frameLoading: {
                      frameIndex,
                      isLoading: false,
                    },
                  })
                );
              }

              if (data.isLast) {
                if (originalTourId) {
                  updateTourWithNewFrames({
                    organizationId,
                    id: Number.parseInt(originalTourId),
                    newTourId: tourId,
                    insertIndex: insertIndex,
                  })
                    .unwrap()
                    .then(() => {
                      communicationService.removeMessageListener(onPostMessage);
                      tabManager?.closeGlobalActiveTab?.();
                      navigateToTourEditor({
                        tourEditorTabId,
                        id: originalTourId,
                        selectedFrameIndex: insertIndex,
                        skipVisibilityCheck: true,
                        workspaceId: targetWorkspaceId.current ?? workspaceId,
                      });
                    })
                    .catch(() => {
                      notify.error('Error adding new frames to the tour');
                    });

                  return;
                }
                updateTourStatus({
                  organizationId,
                  id: tourId,
                  status: 'recording_finished',
                })
                  .unwrap()
                  .then(() => {
                    communicationService.removeMessageListener(onPostMessage);
                    tabManager?.closeGlobalActiveTab?.();
                    navigateToTourEditor({
                      id: tourId,
                      tourEditorTabId,
                      workspaceId: targetWorkspaceId.current ?? workspaceId,
                    });
                  })
                  .catch(() => {
                    notify.error('Error updating tour status to complete');
                  });
              }
            })
            .catch(() => {
              dispatch(
                updateFramesLoading({
                  uuid,
                  frameLoading: {
                    frameIndex,
                    isLoading: false,
                  },
                })
              );
              handleFirstFrameFailedCapturing(frameIndex === 0);
            });
        })
        .catch(() => {
          handleFirstFrameFailedCapturing(frameIndex === 0);
        });
    }
  };

  const handleDimensionChange = useCallback(
    (dimension) => {
      const { width, height, type } = dimension;

      const { fitFactor } = handleDimensionChangeUtil(
        { width: width, height: height },
        containerRef?.current
      );

      onDimensionChange?.({ width, height }, type, fitFactor);

      analytics.trackEvent(
        AnalyticsTrackEventName.TOUR_SELECT_DIMENSIONS_SETUP_DEVICE,
        {
          location: 'tour_capture_navbar_demo_based',
          checkedItem: type,
          tourId,
          timestamp: Date.now(),
        }
      );
    },
    [analytics, onDimensionChange, tourId]
  );

  const showWebView = webViewFrame && !firstFrameFailedCapturing;

  return (
    <div
      ref={containerRef}
      className="h-full w-full relative"
    >
      {(iframeRef || webViewRef) && (
        <DimensionPanelWrapper
          iframeEl={iframeRef?.current}
          anchorRef={isDesktop ? webViewRef : iframeRef}
          visible={isShowingDimensionPanel}
          activeTabId={activeTabId}
          offset={[-20, -580]}
          containerRef={containerRef}
          onClose={closeDimensionsPanel}
          onSubmit={(dimension) => {
            dispatch(
              setIsShowingDimensionPanel({
                uuid,
                isShowingDimensionPanel: !isShowingDimensionPanel,
              })
            );

            analytics.trackEvent(
              AnalyticsTrackEventName.TOUR_CLICK_DIMENSIONS_SETUP_APPLY,
              {
                location: 'tour_capture_navbar_live',
                checkedItem: dimension.type,
                tourId,
                timestamp: Date.now(),
              }
            );
          }}
          onDimensionChange={handleDimensionChange}
        />
      )}
      {showLoadingDots && (
        <div className="h-full w-full absolute flex justify-center items-center bg-ds-accent3-100 cursor-not-allowed">
          <LoadingDots color={ColorHex.natural600} />
        </div>
      )}
      {firstFrameFailedCapturing && (
        <FirstFrameFailedCapturingModal
          anchorRef={containerRef}
          open={firstFrameFailedCapturing}
          onConfirm={handleRestartCapturingAfterFirstFrameFailedCapturing}
        />
      )}
      {showWebView && webViewFrame}
      {!webViewFrame && (
        <iframe
          title="demostack-iframe"
          className="w-full h-full"
          name="demostack-iframe"
          ref={iframeRef}
          id={CAPTURE_IFRAME_ID}
          src={`${dynamicRecordingUrl}?mimesis_replicate${
            mimesisReplicateUrl ? `=${mimesisReplicateUrl}` : ''
          }&demosgw_token=${playerToken}&first_url=${url}&analytics_session_id=${analytics.userData.anonymousId()}`}
        />
      )}
    </div>
  );
};

export default TourCaptureDynamicRecorderFromUrl;
